import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Star } from 'lucide-react';
import api, { isAuthenticated } from '../utils/api';
import { toast } from 'sonner';
import { normalizeImageUrl, normalizeImages } from '../utils/imageUtils';

const MAX_IMAGES = 4;
const IMAGE_LABELS = ['Main', 'Slide 2', 'Slide 3', 'Slide 4'];

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    subcategory: '',
    color: '',
    price: 0,
    hide_price: true,
    images: [],
    description: '',
    instagram_video_url: '',
    featured: false,
    status: 'published'
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!formData.category) { setSubcategoryOptions([]); return; }
    const fetchSubs = async () => {
      try {
        const cat = categories.find(c => c.name === formData.category);
        if (!cat) { setSubcategoryOptions([]); return; }
        const res = await api.get(`/subcategories?category_slug=${cat.slug}`);
        setSubcategoryOptions(res.data || []);
      } catch (e) { setSubcategoryOptions([]); }
    };
    fetchSubs();
  }, [formData.category, categories]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products?limit=100');
      setProducts(response.data);
    } catch (error) { console.error('Error fetching products:', error); }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) { console.error('Error fetching categories:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, price: 0, hide_price: true };
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.sku}`, payload);
        toast.success('Product updated successfully!');
      } else {
        await api.post('/products', payload);
        toast.success('Product created successfully!');
      }
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) { toast.error('Error saving product'); }
  };

  const handleDelete = async (sku) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/products/${sku}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) { toast.error('Error deleting product'); }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      category: product.category,
      subcategory: product.subcategory,
      color: product.color,
      price: 0,
      hide_price: true,
      images: product.images || [],
      description: product.description || '',
      instagram_video_url: product.instagram_video_url || '',
      featured: product.featured || false,
      status: product.status || 'published'
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      subcategory: '',
      color: '',
      price: 0,
      hide_price: true,
      images: [],
      description: '',
      instagram_video_url: '',
      featured: false,
      status: 'published'
    });
    setSubcategoryOptions([]);
  };

  const handleImageUrlAdd = () => {
    if (formData.images.length >= MAX_IMAGES) return;
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      setFormData({ ...formData, images: [...formData.images, url.trim()] });
    }
  };

  const handleImageRemove = (idx) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== idx) });
  };

  // Drag-to-reorder
  const handleDragStart = (e, idx) => e.dataTransfer.setData('dragIdx', String(idx));
  const handleDrop = (e, dropIdx) => {
    const dragIdx = parseInt(e.dataTransfer.getData('dragIdx'), 10);
    if (dragIdx === dropIdx) return;
    const updated = [...formData.images];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(dropIdx, 0, moved);
    setFormData({ ...formData, images: updated });
  };
  const handleDragOver = (e) => e.preventDefault();

  const hasSubOptions = subcategoryOptions.length > 0;
  const canAddMore = formData.images.length < MAX_IMAGES;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-[#E5A93C] hover:text-[#2A0000]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-[#2A0000]">Product Management</h1>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingProduct(null); resetForm(); }}
            className="flex items-center space-x-2 btn-primary"
            data-testid="add-product-button"
          >
            <Plus className="w-5 h-5" />
            <span>Add Product</span>
          </button>
        </div>
      </header>

      <div className="p-6">
        {/* ── Form Modal ── */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-[#2A0000] mb-6">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-2">Product Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                    placeholder="e.g., Royal Red Banaras Saree"
                  />
                </div>

                {/* Category + Subcategory */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat.slug} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Subcategory *
                      {hasSubOptions && (
                        <span className="ml-2 text-xs font-normal text-[#E5A93C]">(sizes available)</span>
                      )}
                    </label>
                    {hasSubOptions ? (
                      <select
                        value={formData.subcategory}
                        onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                      >
                        <option value="">Select subcategory / size</option>
                        {subcategoryOptions.map(sub => (
                          <option key={sub.slug} value={sub.name}>{sub.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={formData.subcategory}
                        onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                        placeholder="e.g., Banaras Saree"
                      />
                    )}
                    {formData.category === 'Chudidhar' && !hasSubOptions && (
                      <p className="text-xs text-amber-600 mt-1">
                        No sizes found. Go to Categories → Chudidhar → Add 3XL/4XL/5XL first.
                      </p>
                    )}
                  </div>
                </div>

                {/* Size badge preview */}
                {formData.subcategory && ['3XL', '4XL', '5XL'].includes(formData.subcategory) && (
                  <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded">
                    <span className="px-2 py-1 text-xs font-bold bg-[#2A0000] text-white rounded">{formData.subcategory}</span>
                    <span className="text-sm text-amber-800">
                      This product will appear under Chudidhar → {formData.subcategory} in the navbar
                    </span>
                  </div>
                )}

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium mb-2">Color *</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                    placeholder="e.g., Rani Pink"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                    placeholder="Product details..."
                  />
                </div>

                {/* Instagram Video URL */}
                <div>
                  <label className="block text-sm font-medium mb-2">Instagram Video URL</label>
                  <input
                    type="url"
                    value={formData.instagram_video_url}
                    onChange={(e) => setFormData({ ...formData, instagram_video_url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E5A93C]"
                    placeholder="e.g., https://www.instagram.com/reel/xxxxxxxxx/"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Paste the reel/video link. Customers will see a “Watch on Instagram” link on the product page.
                  </p>
                </div>

                {/* ── Images ── */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">
                      Product Images
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        ({formData.images.length}/{MAX_IMAGES})
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={handleImageUrlAdd}
                      disabled={!canAddMore}
                      className={`flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-sm border transition-colors ${
                        canAddMore
                          ? 'text-[#E5A93C] border-[#E5A93C] hover:bg-[#E5A93C] hover:text-white'
                          : 'text-gray-300 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add URL
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mb-3">
                    First image is the main display. Drag thumbnails to reorder. HEIC/iPhone photos supported.
                  </p>

                  {/* 4-slot image grid */}
                  <div className="grid grid-cols-4 gap-2">
                    {Array.from({ length: MAX_IMAGES }).map((_, idx) => {
                      const img = formData.images[idx];
                      const isNextEmpty = idx === formData.images.length;

                      return img ? (
                        /* ── Filled slot ── */
                        <div
                          key={idx}
                          draggable
                          onDragStart={(e) => handleDragStart(e, idx)}
                          onDrop={(e) => handleDrop(e, idx)}
                          onDragOver={handleDragOver}
                          className="relative rounded border border-gray-200 overflow-hidden bg-gray-100 cursor-grab active:cursor-grabbing"
                          style={{ aspectRatio: '3/4' }}
                          title="Drag to reorder"
                        >
                          <img
                            src={normalizeImageUrl(img)}
                            alt={`Image ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          {/* Error fallback */}
                          <div
                            className="w-full h-full items-center justify-center text-[10px] text-gray-400 text-center p-1 hidden"
                            style={{ position: 'absolute', inset: 0, background: '#f3f4f6' }}
                          >
                            Preview<br />unavailable
                          </div>

                          {/* Label badge */}
                          <span
                            className="absolute top-1 left-1 text-[9px] font-bold px-1 py-0.5 rounded"
                            style={{ background: idx === 0 ? '#E5A93C' : 'rgba(42,0,0,0.72)', color: '#fff' }}
                          >
                            {IMAGE_LABELS[idx]}
                          </span>

                          {/* Remove */}
                          <button
                            type="button"
                            onClick={() => handleImageRemove(idx)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-xs leading-none transition-colors"
                            title="Remove"
                          >
                            ×
                          </button>

                          {/* Drag hint */}
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] text-white/50 select-none pointer-events-none">
                            ⠿ drag
                          </div>
                        </div>
                      ) : (
                        /* ── Empty slot ── */
                        <button
                          key={idx}
                          type="button"
                          onClick={isNextEmpty ? handleImageUrlAdd : undefined}
                          disabled={!isNextEmpty}
                          className={`rounded border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
                            isNextEmpty
                              ? 'border-[#E5A93C]/50 hover:border-[#E5A93C] hover:bg-amber-50 cursor-pointer'
                              : 'border-gray-100 cursor-not-allowed opacity-30'
                          }`}
                          style={{ aspectRatio: '3/4' }}
                        >
                          <Plus className={`w-5 h-5 ${isNextEmpty ? 'text-[#E5A93C]' : 'text-gray-300'}`} />
                          <span className={`text-[10px] font-medium ${isNextEmpty ? 'text-[#E5A93C]' : 'text-gray-300'}`}>
                            {IMAGE_LABELS[idx]}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Progress indicator */}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${(formData.images.length / MAX_IMAGES) * 100}%`,
                          background: formData.images.length === MAX_IMAGES ? '#22c55e' : '#E5A93C'
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formData.images.length === MAX_IMAGES
                        ? '✓ All 4 added'
                        : `${MAX_IMAGES - formData.images.length} slot${MAX_IMAGES - formData.images.length !== 1 ? 's' : ''} left`}
                    </span>
                  </div>
                </div>

                {/* Featured + Status */}
                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Featured Product</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <span className="text-sm">Status:</span>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="px-3 py-1 border border-gray-300 rounded-sm text-sm"
                    >
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                    </select>
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingProduct(null); resetForm(); }}
                    className="flex-1 btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Products Table ── */}
        {loading ? (
          <div className="text-center py-20"><p className="text-gray-600">Loading products...</p></div>
        ) : products.length === 0 ? (
          <div className="text-center py-20"><p className="text-gray-600 mb-4">No products yet. Add your first product!</p></div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Product', 'SKU', 'Category', 'Subcategory', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.sku} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative flex-shrink-0">
                          <img
                            src={normalizeImageUrl(product.images?.[0] || 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg')}
                            alt={product.title}
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg';
                            }}
                          />
                          {/* Image count badge */}
                          {product.images?.length > 1 && (
                            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#E5A93C] text-white text-[9px] font-bold flex items-center justify-center">
                              {product.images.length}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#2C2C2C]">{product.title}</p>
                          <p className="text-sm text-gray-500">{product.color}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">{product.sku}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{product.category}</td>
                    <td className="px-6 py-4">
                      {['3XL', '4XL', '5XL'].includes(product.subcategory) ? (
                        <span className="px-2 py-0.5 text-xs font-bold bg-[#2A0000] text-white rounded">{product.subcategory}</span>
                      ) : (
                        <span className="text-sm text-gray-600">{product.subcategory}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          product.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>{product.status}</span>
                        {product.featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button onClick={() => handleEdit(product)} className="text-blue-600 hover:text-blue-800" data-testid={`edit-${product.sku}`}>
                          <Edit className="w-5 h-5" />
                        </button>
                        <button onClick={() => handleDelete(product.sku)} className="text-red-600 hover:text-red-800" data-testid={`delete-${product.sku}`}>
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProductsPage;